/**
 * Centralized color configuration for all chart components
 * Use with Join() function to apply consistent styling
 */

// Next.js
import { IBM_Plex_Mono } from 'next/font/google'

export const ibmPlexMono = IBM_Plex_Mono({
  weight: ['400', '700'],
  subsets: ['latin'],
})

export const chartStyles = {
  typography: {
    title: 'text-3xl font-normal text-blue-900 dark:text-white',
    description: 'text-sm text-slate-600 dark:text-slate-400',
    descriptionCentered:
      'mx-auto mt-4 max-w-2xl text-lg text-slate-600 dark:text-slate-400',
    label:
      'text-sm font-normal text-slate-300/70 transition-colors group-hover:text-white group-data-selected:text-slate-900 dark:group-data-selected:text-white',
  },
  container: {
    base: 'rounded-2xl lg:p-8',
    dark: '',
  },
  tabPanel: {
    base: 'rounded-xl border border-teal-950',
  },

  tabList: {
    base: 'flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 rounded-xl bg-slate-100 dark:bg-slate-800 p-1 mb-1',
  },

  tab: {
    base: 'px-4 py-2.5 text-sm font-medium leading-5 rounded-lg cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30 transition-all',
    selected: 'bg-blue-600 text-white shadow-md',
    unselected:
      'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400',
  },

  tooltip: {
    base: 'rounded-lg border border-slate-300 bg-white/60 backdrop-blur-md p-3 shadow-lg',
    dark: 'dark:border-slate-300/70 dark:bg-slate-800/30',
  },

  tooltipText: {
    heading: 'font-semibold text-slate-900 dark:text-slate-100',
    body: 'text-xs text-slate-600 dark:text-slate-300',
    value: 'text-slate-900 dark:text-slate-100',
  },

  chartText: {
    axis: '[&_text]:fill-slate-700 dark:[&_text]:fill-white',
    axisAlt: '[&_text]:fill-slate-700 dark:[&_text]:fill-white',
  },

  cartesianGrid: {
    className: 'stroke-0.5 dark:stroke-slate-400 stroke-slate-700',
  },

  svgFill: {
    text: 'fill-slate-700 dark:fill-white',
  },

  tickColors: {
    light: '#334155', // slate-700
    dark: '#ffffff', // white
  },

  hoverBackground: {
    light: '#dc2626', // red-600
    dark: '#0d9488', // teal-600
    opacity: '0.2',
  },

  barStroke: {
    light: '#1e293b', // slate-800 - dark stroke for light theme
    dark: '#f1f5f9', // slate-100 - light stroke for dark theme
  },
  barFillOpacity: 0.9, // Opacity for bar fills to show color with transparency

  // Unified tick style — pass to tick={{ }} prop on XAxis/YAxis/PolarRadiusAxis
  tickStyle: {
    fontSize: 12,
    fontFamily: "'Space Grotesk', sans-serif",
    className: 'fill-slate-700 dark:fill-white',
  },

  // Unified axis label styling for both X and Y axes (18px, semibold)
  // For Y-axis, add angle: -90 to the style object
  axisLabel: {
    style: {
      textAnchor: 'middle',
      fontFamily: "'Space Grotesk', sans-serif",
    } as React.CSSProperties,
    className: 'text-lg font-semibold fill-slate-600 dark:fill-white',
  },

  // Legend styling (from Grouped Comparison)
  legend: {
    wrapperStyle: {
      paddingTop: '32px',
      fontSize: '16px',
      lineHeight: '24px',
      fontFamily: "'Space Grotesk', sans-serif",
    } as React.CSSProperties,
    iconSize: 16,
  },

  boxPlot: {
    fill: '#a6cee3', // Light blue for light mode
    fillDark: '#60a5fa', // blue-400 for dark mode (lighter)
    stroke: '#1e293b', // Dark stroke for light mode
    strokeDark: '#cbd5e1', // slate-300 for dark mode (lighter)
    medianStroke: '#ef4444', // red-500 for median line
    outlierFill: '#fdbe6f', // Orange for outliers
    outlierStroke: '#1e293b', // Dark stroke for outliers
    outlierStrokeDark: '#cbd5e1', // slate-300 for dark mode
    fillOpacity: 0.8, // 80% opacity for box fills
  },
} as const

// Model colors used across all charts - colors matched from chart
export const modelColors = {
  'anthropic/ claude-opus-4.6': '#8b5cf6', // violet-500 (purple)
  'anthropic/ claude-opus-4.5': '#ec4899', // pink-500
  'anthropic/ claude-sonnet-4.5': '#f97316', // orange-500
  'google/gemini-3-pro': '#ef4444', // red-500
  'openai/ gpt-5.2': '#14b8a6', // teal-500 (teal green)
  'x-ai/ grok-code-fast-1': '#94a3b8', // slate-400 (lighter steel blue for dark mode)
  'LLM + CodeLogician': '#3b82f6', // blue-500
} as const

// Model average colors (without space after slash) - colors matched from chart
export const modelAverageColors: Record<string, string> = {
  'anthropic/claude-opus-4.6': '#8b5cf6', // violet-500 (purple)
  'anthropic/claude-opus-4.5': '#ec4899', // pink-500
  'anthropic/claude-sonnet-4.5': '#f97316', // orange-500
  'google/gemini-3-pro': '#ef4444', // red-500
  'openai/gpt-5.2': '#14b8a6', // teal-500 (teal green)
  'x-ai/grok-code-fast-1': '#94a3b8', // slate-400 (lighter steel blue for dark mode)
}

// Metric colors (ordered to match chart display)
export const metricColorMap: Record<string, string> = {
  'State Space Estimation': '#3b82f6', // blue-500
  'Control Flow Understanding': '#14b8a6', // teal-500
  'Edge Case Detection': '#8b5cf6', // violet-500
  'Decision Boundary': '#06b6d4', // cyan-500
  'Decision Boundary Clarity': '#06b6d4', // cyan-500 (alias)
  'Outcome Precision': '#6366f1', // indigo-500
  'Direction Accuracy': '#10b981', // emerald-500
  'Coverage Completeness': '#f59e0b', // amber-500
}

// Base model color for all comparison charts (Model Averages, Metric Averages, Median Scores)
export const baseModelLegendColor = '#cbd5e1' // slate-300
